import { Button, FlexBox, Spinner, Text } from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { Fragment, type ReactNode, useCallback, useMemo } from 'react';

import {
  auditLogsApi,
  ConversationAuditLogEntry,
  ConversationFlowAuditLogEntry,
} from 'apis/auditLogs';
import { useLazyPaginatedEndpoint } from 'utils/apis/pagination/useLazyPaginatedEndpoint/useLazyPaginatedEndpoint';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import styles from '../activityLog.module.scss';

import { LogEntry } from './logEntry';
import { NodeErrors } from './nodeErrors';
import {
  RoutedThroughAgentAvailability,
  RoutedThroughAnnouncement,
  RoutedThroughAutoReply,
  RoutedThroughBusinessHours,
  RoutedThroughCallRecording,
  RoutedThroughChatbot,
  RoutedThroughClose,
  RoutedThroughContactForm,
  RoutedThroughDialInput,
  RoutedThroughFilter,
  RoutedThroughForward,
  RoutedThroughIntegration,
  RoutedThroughIntentDetection,
  RoutedThroughIVRMenu,
  RoutedThroughLocation,
  RoutedThroughMessengerInput,
  RoutedThroughMessengerMenu,
  RoutedThroughMessengerMessage,
  RoutedThroughMessengerUrlRouting,
  RoutedThroughQueue,
  RoutedThroughTags,
  RoutedThroughVoicemail,
} from './routedThroughNodes';

type RoutedThroughFlowProps = {
  entry: ConversationAuditLogEntry<'CONVERSATION_ROUTED_THROUGH_FLOW'>;
};

function getRoutedNode(node: ConversationFlowAuditLogEntry): ReactNode {
  switch (node.activity_type) {
    case 'EMAIL_EXTERNAL_DATA_ROUTING_NODE_VISITED':
    case 'TELEPHONY_EXTERNAL_DATA_ROUTING_NODE_VISITED':
    case 'MESSENGER_EXTERNAL_DATA_ROUTING_NODE_VISITED':
      return <RoutedThroughFilter node={node} />;
    case 'PHONE_NUMBER_ROUTING_NODE_VISITED':
      return <RoutedThroughLocation node={node} />;
    case 'IVR_MENU_NODE_VISITED':
      return <RoutedThroughIVRMenu node={node} />;
    case 'EMAIL_BUSINESS_HOURS_NODE_VISITED':
    case 'TELEPHONY_BUSINESS_HOURS_NODE_VISITED':
      return <RoutedThroughBusinessHours node={node} />;
    case 'MESSENGER_MENU_NODE_VISITED':
      return <RoutedThroughMessengerMenu node={node} />;
    case 'EMAIL_QUEUE_NODE_VISITED':
    case 'TELEPHONY_QUEUE_NODE_VISITED':
    case 'MESSENGER_QUEUE_NODE_VISITED':
      return <RoutedThroughQueue node={node} />;
    case 'FORWARD_NODE_VISITED':
      return <RoutedThroughForward node={node} />;
    case 'VOICE_MAIL_NODE_VISITED':
      return <RoutedThroughVoicemail node={node} />;
    case 'EMAIL_EXTERNAL_INTEGRATION_NODE_VISITED':
    case 'TELEPHONY_EXTERNAL_INTEGRATION_NODE_VISITED':
    case 'MESSENGER_EXTERNAL_INTEGRATION_NODE_VISITED':
      return <RoutedThroughIntegration node={node} />;
    case 'CALL_RECORDING_NODE_VISITED':
      return <RoutedThroughCallRecording node={node} />;
    case 'AUTO_REPLY_NODE_VISITED':
      return <RoutedThroughAutoReply node={node} />;
    case 'EMAIL_TAG_NODE_VISITED':
    case 'TELEPHONY_TAG_NODE_VISITED':
    case 'MESSENGER_TAG_NODE_VISITED':
      return <RoutedThroughTags node={node} />;
    case 'INTENT_DETECTION_NODE_VISITED':
      return <RoutedThroughIntentDetection node={node} />;
    case 'MESSENGER_AGENT_AVAILABILITY_NODE_VISITED':
      return <RoutedThroughAgentAvailability node={node} />;
    case 'EXTERNAL_DATA_PHONE_INPUT_NODE_VISITED':
      return <RoutedThroughDialInput node={node} />;
    case 'MESSENGER_INPUT_FIELD_NODE_VISITED':
      return <RoutedThroughMessengerInput node={node} />;
    case 'MESSENGER_URL_ROUTING_NODE_VISITED':
      return <RoutedThroughMessengerUrlRouting node={node} />;
    case 'MESSENGER_MESSAGE_NODE_VISITED':
      return <RoutedThroughMessengerMessage />;
    case 'MESSENGER_CONTACT_FORM_NODE_VISITED':
    case 'MESSENGER_FORM_NODE_AS_CONTACT_FORM_VISITED':
      return <RoutedThroughContactForm />;
    case 'ANNOUNCEMENT_NODE_VISITED':
      return <RoutedThroughAnnouncement node={node} />;
    case 'FLOW_CHATBOT_NODE_VISITED':
      return <RoutedThroughChatbot node={node} />;
    case 'EMAIL_CLOSE_NODE_VISITED':
    case 'MESSENGER_CLOSE_NODE_VISITED':
      return <RoutedThroughClose node={node} />;
    default:
      return 'Unrecognized action';
  }
}

function getRoutedGroupedNodes(
  nodes: ConversationFlowAuditLogEntry[],
): ReactNode {
  if (nodes.length === 0) {
    return (
      <Text.Paragraph variant="2" subdued>
        Unrecognized action
      </Text.Paragraph>
    );
  }
  const lastNode = nodes[nodes.length - 1];
  switch (lastNode.activity_type) {
    case 'FLOW_CHATBOT_NODE_VISITED':
      return (
        <>
          <RoutedThroughChatbot node={lastNode} />
          <NodeErrors
            expressionErrors={lastNode.flow_node_attributes.expression_errors}
            scopeVariableSaveErrors={
              lastNode.flow_node_attributes.scope_variable_save_errors
            }
          />
        </>
      );
    default:
      return nodes.map((node, index) => (
        <Fragment key={index}>
          <Text.Paragraph variant="2" subdued>
            {getRoutedNode(node)}
          </Text.Paragraph>
          <NodeErrors
            expressionErrors={node.flow_node_attributes.expression_errors}
            scopeVariableSaveErrors={
              node.flow_node_attributes.scope_variable_save_errors
            }
          />
        </Fragment>
      ));
  }
}

const channelPathMap: Record<string, string> = {
  phone: 'phone',
  email: 'email',
  messenger: 'dixa-messenger',
};

export const RoutedThroughFlow = ({
  entry,
}: RoutedThroughFlowProps): JSX.Element => {
  const navigate = useAppNavigate();
  const {
    items: data,
    changeArg,
    asyncStatus,
    hasNextPage,
    nextPage,
  } = useLazyPaginatedEndpoint({
    endpoint: auditLogsApi.endpoints.readConversationFlowAuditLogs,
    itemsSelector: (response) => response.data.logs,
    infinite: true,
  });

  // Only fetch the flow logs the first time the user expands the section, to
  // avoid firing a request for every routed-through-flow entry in the log.
  const handleExpandedChange = useCallback(
    (isExpanded: boolean) => {
      if (isExpanded && asyncStatus === 'uninitialized') {
        changeArg({
          resourceId: entry.resource.id,
          limit: 1000,
          sort: { key: 'timestamp', direction: 'asc' },
        });
      }
    },
    [asyncStatus, changeArg, entry.resource.id],
  );

  const hasFlowReadAccess = useHasAccess('settings.flow:r');

  const handleGoToFlow = () => {
    navigate({
      path: '/settings/:channel/flow/:flowId',
      params: {
        path: {
          channel: channelPathMap[entry.channel],
          flowId: entry.flow_uuid,
        },
      },
    });
  };

  /**
   * Returns a grouped array of flow activities. The grouping is only based on
   * consecutive node types. Non consecutive node types are not grouped
   * together.
   */
  const groupedActivities = useMemo(
    () =>
      data.reduce((acc: ConversationFlowAuditLogEntry[][], node) => {
        const lastGroup: ConversationFlowAuditLogEntry[] = acc[acc.length - 1];

        if (lastGroup && lastGroup[0].activity_type === node.activity_type) {
          lastGroup.push(node);
        } else {
          acc.push([node]);
        }

        return acc;
      }, []),
    [data],
  );

  return (
    <LogEntry
      entry={entry}
      icon="route"
      text={`Routed through ${entry.channel} flow ${entry.flow_name} ${
        ['phone', 'email'].includes(entry.channel)
          ? `for ${entry.location}`
          : ''
      }`}
      isExpandable
      onExpandedChange={handleExpandedChange}
    >
      <FlexBox
        padding="0 0 0 300"
        margin="0 0 0 500"
        gap="200"
        flexDirection="column"
      >
        {asyncStatus === 'rejected' && (
          <span className={styles.error}>
            Failed to retrieve flow activity log.
          </span>
        )}

        {/* Already-fetched items stay visible while the next page loads. */}
        {groupedActivities.map((group) => getRoutedGroupedNodes(group))}

        {hasFlowReadAccess && (
          <FlexBox margin="300 0">
            <Button variant="secondary" onClick={handleGoToFlow}>
              Go to flow
            </Button>
          </FlexBox>
        )}

        {asyncStatus === 'pending' && !groupedActivities.length && (
          <div className={styles.spinnerContainer} data-testid="spinner">
            <Spinner />
          </div>
        )}

        {groupedActivities.length > 0 && hasNextPage && (
          <FlexBox margin="200 0">
            <Button
              variant="secondary"
              isLoading={asyncStatus === 'pending'}
              onClick={nextPage}
            >
              Load more
            </Button>
          </FlexBox>
        )}
      </FlexBox>
    </LogEntry>
  );
};
